import React, { useState, useEffect, useCallback } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import * as adminService from '../services/adminService'
import { NavLink } from 'react-router-dom'
import {
  FiAward,
  FiBarChart2,
  FiDatabase,
  FiInbox,
  FiLogOut,
  FiMenu,
  FiMessageSquare,
  FiSearch,
  FiShield,
  FiLoader,
  FiAlertTriangle,
  FiUsers,
} from 'react-icons/fi'
import './AdminDashboardPage.css' // Reuse the same CSS for a consistent look

// --- Reusable Components from AdminDashboardPage ---
const AdminSidebar = ({ user, logout }) => (
  <>
    <nav className="admin-sidebar-nav">
      <NavLink to="/admin/dashboard" className="admin-nav-link" end>
        <FiBarChart2 size={20} /> Dashboard
      </NavLink>
      <NavLink to="/admin/kb" className="admin-nav-link">
        <FiDatabase size={20} /> Knowledge
      </NavLink>
      {user?.roles?.includes(
        process.env.REACT_APP_SUPERADMIN_ROLE_NAME || 'superadmin'
      ) && (
        <NavLink to="/admin/features" className="admin-nav-link">
          <FiAward size={20} /> Features
        </NavLink>
      )}
      <NavLink to="/admin/privacy" className="admin-nav-link">
        <FiShield size={20} /> Privacy
      </NavLink>
      <NavLink to="/admin/feedback" className="admin-nav-link">
        <FiInbox size={20} /> Feedback
      </NavLink>
      <NavLink to="/chat" className="admin-nav-link">
        <FiMessageSquare size={20} /> Chat
      </NavLink>
    </nav>
    <div className="admin-sidebar-footer">
      <div className="admin-user-info">
        {user && (
          <p className="admin-user-display-name" title={user.display_name}>
            {user.display_name || user.first_name || 'Utente'}
          </p>
        )}
      </div>
      <div className="admin-sidebar-footer-actions">
        <button onClick={logout} className="admin-icon-button small" title="Esci">
          <FiLogOut />
        </button>
      </div>
    </div>
  </>
)

const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <label className="toggle-switch">
    <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
    <span className="slider round"></span>
  </label>
)

// --- Main Feature Flag Manager Component ---
const FeatureFlagManager = () => {
  const { token, user, logout, fetchUser } = useAuthContext()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [updatingFlagsFor, setUpdatingFlagsFor] = useState(null) // Tracks which user's flags are being updated
  const [updatingTeamFor, setUpdatingTeamFor] = useState(null) // Tracks which user's team is being updated

  const [isSidebarOpen, setSidebarOpen] = useState(false)

  const isSuperAdmin = user?.roles?.includes(
    process.env.REACT_APP_SUPERADMIN_ROLE_NAME || 'superadmin'
  )

  // Define available feature flags here for easy management
  const AVAILABLE_FLAGS = [
    { key: 'image_generation', label: 'Image Gen' },
    { key: 'presentation_generation', label: 'Presentation Gen' },
    { key: 'video_generation', label: 'Video Gen' },
    { key: 'cad_generation', label: 'CAD Gen' },
    { key: 'call_feature', label: 'Call' },
    { key: 'screen_share', label: 'Screen Share' },
  ]

  // Define available teams for assignment
  const AVAILABLE_TEAMS = ['SFRE', 'SFE', 'SFCM', 'SFS', 'SFSE']

  const fetchUsers = useCallback(async () => {
    if (!token || !isSuperAdmin) return
    setLoading(true)
    setError('')
    try {
      // We use a high limit here, but for a very large user base,
      // pagination would be added just like in the main dashboard.
      const response = await adminService.getAllUsersStats(token, {
        search: searchTerm,
        limit: 100, // Fetch up to 100 users matching search
      })
      setUsers(response.users || [])
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError(err.response?.data?.detail || 'Could not load users.')
    } finally {
      setLoading(false)
    }
  }, [token, isSuperAdmin, searchTerm])

  useEffect(() => {
    // Debounce search term to avoid too many API calls
    const handler = setTimeout(() => {
      fetchUsers()
    }, 500) // 500ms delay

    return () => {
      clearTimeout(handler)
    }
  }, [searchTerm, fetchUsers])

  const handleToggleFlag = async (userId, flagKey) => {
    const userToUpdate = users.find(u => u.id === userId)
    if (!userToUpdate) return

    setUpdatingFlagsFor(userId)
    setError('')

    const currentFlags = userToUpdate.feature_flags || []
    const newFlags = currentFlags.includes(flagKey)
      ? currentFlags.filter(f => f !== flagKey)
      : [...currentFlags, flagKey]

    try {
      await adminService.updateUserFeatureFlags(token, userId, newFlags)
      // Update local state for instant UI feedback
      setUsers(prevUsers =>
        prevUsers.map(u => (u.id === userId ? { ...u, feature_flags: newFlags } : u))
      )
    } catch (err) {
      console.error(`Failed to update flags for user ${userId}:`, err)
      setError(
        `Failed to update flags for ${userToUpdate.sfre_email}. Please try again.`
      )
      // Revert UI on failure
      setTimeout(() => setError(''), 5000)
    } finally {
      setUpdatingFlagsFor(null)
    }
  }

  const handleTeamChange = async (userId, newTeam) => {
    const userToUpdate = users.find(u => u.id === userId)
    if (!userToUpdate) return

    // Convert empty string to null for "no team"
    const teamToAssign = newTeam === '' ? null : newTeam

    setUpdatingTeamFor(userId)
    setError('')

    try {
      await adminService.assignUserTeam(token, userId, teamToAssign)
      // Update local state for instant UI feedback
      setUsers(prevUsers =>
        prevUsers.map(u => (u.id === userId ? { ...u, team: teamToAssign } : u))
      )

      // If the current user's team was changed, refresh their data
      if (user && user.id === userId) {
        await fetchUser(token)
      }
    } catch (err) {
      console.error(`Failed to update team for user ${userId}:`, err)
      setError(
        `Failed to update team for ${userToUpdate.sfre_email}. Please try again.`
      )
      // Clear error after 5 seconds
      setTimeout(() => setError(''), 5000)
    } finally {
      setUpdatingTeamFor(null)
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="admin-page-container">
        <main
          className="admin-main-content"
          style={{ margin: 'auto', maxWidth: '600px' }}
        >
          <div className="admin-card" style={{ textAlign: 'center' }}>
            <FiAlertTriangle size={48} className="danger" />
            <h2>Accesso Negato</h2>
            <p>Solo i Super Admin possono gestire le feature flags.</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="admin-page-container">
      {isSidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-sidebar-header">
          <img
            src="/franca-logo.png"
            alt="F.R.A.N.C.A."
            className="admin-sidebar-logo"
          />
        </div>
        <AdminSidebar user={user} logout={logout} />
      </aside>

      <main className="admin-main-content">
        <header className="admin-page-header">
          <button
            className="admin-button mobile-menu-button"
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu />
          </button>
          <h1>
            <FiUsers /> Gestione Utenti
          </h1>
          <p
            style={{
              color: 'var(--text-secondary)',
              fontSize: '14px',
              marginTop: '8px',
            }}
          >
            Gestisci feature flags e assegnazioni team degli utenti
          </p>
        </header>

        {error && <div className="admin-error-message">{error}</div>}

        <section className="admin-card">
          <h2>Abilita Features per Utenti</h2>
          <p>
            Cerca un utente per abilitare o disabilitare l'accesso a features
            sperimentali come la generazione di immagini, video, chiamate vocali e
            condivisione schermo.
          </p>
          <div className="admin-filters-bar">
            <div className="admin-search-input">
              <FiSearch />
              <input
                type="text"
                placeholder="Cerca utente per nome o email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className={`admin-table-container ${loading ? 'is-loading' : ''}`}>
            <table>
              <thead>
                <tr>
                  <th>Email Utente</th>
                  <th>Nome</th>
                  <th>Team</th>
                  {AVAILABLE_FLAGS.map(flag => (
                    <th key={flag.key}>{flag.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={3 + AVAILABLE_FLAGS.length}
                      className="admin-loading-state"
                    >
                      <FiLoader className="spinner-icon" /> Caricamento...
                    </td>
                  </tr>
                ) : users.length > 0 ? (
                  users.map(u => (
                    <tr key={u.id}>
                      <td>{u.sfre_email}</td>
                      <td>{u.display_name || 'N/A'}</td>
                      <td>
                        <select
                          value={u.team || ''}
                          onChange={e => handleTeamChange(u.id, e.target.value)}
                          disabled={updatingTeamFor === u.id}
                          className="admin-select"
                          style={{ minWidth: '120px' }}
                        >
                          <option value="">Nessun team</option>
                          {AVAILABLE_TEAMS.map(team => (
                            <option key={team} value={team}>
                              {team}
                            </option>
                          ))}
                        </select>
                        {updatingTeamFor === u.id && (
                          <FiLoader
                            className="spinner-icon"
                            style={{ marginLeft: '8px' }}
                          />
                        )}
                      </td>
                      {AVAILABLE_FLAGS.map(flag => (
                        <td key={flag.key}>
                          <ToggleSwitch
                            checked={u.feature_flags?.includes(flag.key)}
                            onChange={() => handleToggleFlag(u.id, flag.key)}
                            disabled={updatingFlagsFor === u.id}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3 + AVAILABLE_FLAGS.length} className="admin-no-data">
                      Nessun utente trovato. Prova a modificare la ricerca.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  )
}

export default FeatureFlagManager
